<template>
  <div class="container page" :class="{ 'pl-0 pt-0 pr-0': $vuetify.breakpoint.xsOnly }">
    <AppToolbar
      :title="$t('message.organizations')"
      :toggle-drawer="$vuetify.breakpoint.smAndDown"
      @toggleDrawer="$emit('toggleDrawer')"
    >
      <template slot="buttons">
        <ToolbarView :map-route="{ name: 'OrganizationListMap' }"></ToolbarView>
      </template>
    </AppToolbar>

    <template v-if="error || deleteError">
      <GenericError></GenericError>
    </template>

    <template v-else>
      <template v-if="isLoading || items.length > 0">
        <template>
          <v-data-table
            class="table-pointer"
            :headers="headers"
            :items="items"
            :options.sync="options"
            :loading="isLoading"
            :server-items-length="totalItems"
            :footer-props="{ 'items-per-page-options': [5, 10, 25, 100] }"
            @click:row="
              $router.push({
                name: 'OrganizationShow',
                params: { id: $iriToId($event['@id']) }
              })
            "
          >
            <template v-slot:item.image="{ item }">
              <v-avatar size="32"
                ><img
                  :src="item.image ? item.image.contentUrl + '?x-request=html' : require('@/assets/no-img.svg')"
                  :alt="item.name"
              /></v-avatar>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-row no-gutters align="center" justify="end">
                <v-btn
                  icon
                  class="mx-0"
                  @click.stop
                  @click.exact="
                    $router.push({
                      name: 'OrganizationShowMap',
                      params: { id: $iriToId(item['@id']) }
                    })
                  "
                >
                  <v-icon color="grey darken-1">mdi-map</v-icon>
                </v-btn>
                <v-btn icon class="mx-0" @click.stop @click.exact="edit(item)">
                  <v-icon color="grey darken-1">mdi-pencil</v-icon>
                </v-btn>
              </v-row>
            </template>
          </v-data-table>
        </template>
      </template>

      <div v-else class="d-flex fill-height text-center align-center justify-center" style="flex: 1">
        <div>
          <v-icon color="grey lighten-2" size="150px">mdi-plus-circle-outline</v-icon>
          <h1 class="headline">Create your first entry</h1>
          <p class="subheading">
            There are no elements yet.
          </p>
        </div>
      </div>

      <v-snackbar v-if="localCreatedItem" v-model="snackbar">
        {{ localCreatedItem.id }} successfully added.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localUpdatedItem" v-model="snackbar">
        {{ localUpdatedItem.id }} successfully updated.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localDeletedItem" v-model="snackbar">
        {{ localDeletedItem.id }} has been deleted.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
    </template>
    <OrganizationDialog
      :show="showOrganizationUpdateDialog"
      :organization="selectedOrganization"
      @close="showOrganizationUpdateDialog = false"
    ></OrganizationDialog>
  </div>
</template>

<script>
import GenericError from '@/components/error/GenericError'
import { mapActions, mapGetters } from 'vuex'
import AppToolbar from '@/components/AppToolbar'
import OrganizationDialog from '@/components/organization/dialog/Dialog'
import ToolbarView from '@/components/ToolbarView'

export default {
  components: {
    ToolbarView,
    OrganizationDialog,
    GenericError,
    AppToolbar
  },

  data: () => ({
    showOrganizationUpdateDialog: false,
    selectedOrganization: null,
    snackbar: false,
    localDeletedItem: null,
    localUpdatedItem: null,
    localCreatedItem: null,
    deleteConfirmShow: false,
    selectedForDelete: null,
    headers: [
      { text: '', value: 'image', sortable: false, width: 1 },
      { text: 'Name', value: 'name', sortable: true },
      { text: 'Actions', value: 'actions', align: 'right', sortable: false }
    ],
    options: {
      page: 1,
      sortBy: ['name'],
      sortDesc: [true],
      itemsPerPage: 25
    }
  }),

  computed: mapGetters({
    deletedItem: 'organization/del/deleted',
    error: 'organization/list/error',
    items: 'organization/list/items',
    isLoading: 'organization/list/isLoading',
    view: 'organization/list/view',
    createdItem: 'organization/create/created',
    updatedItem: 'organization/update/updated',
    deleteError: 'organization/del/error',
    deleteLoading: 'organization/del/isLoading',
    totalItems: 'organization/list/totalItems'
  }),

  watch: {
    deletedItem: function(deleted) {
      if (deleted) {
        this.localDeletedItem = this.deletedItem
        this.snackbar = true
        this.getPaginatedPage(this.options)
        this.reset()
      }
    },

    options: {
      handler: function(options, optionsOld) {
        if (!options) {
          return
        }
        if (
          options.itemsPerPage !== optionsOld.itemsPerPage ||
          options.page !== optionsOld.page ||
          options.sortBy !== optionsOld.sortBy ||
          options.sortDesc !== optionsOld.sortDesc
        ) {
          this.getPaginatedPage(options)
        }
      },
      deep: true
    }
  },

  created() {
    this.$store.commit('mapSetShow', false)
    this.getPaginatedPage(this.options)
  },

  mounted() {
    if (this.deletedItem || this.updatedItem || this.createdItem) {
      this.localDeletedItem = this.deletedItem
      this.localUpdatedItem = this.updatedItem
      this.localCreatedItem = this.createdItem

      this.snackbar = true
    }
    this.reset()
  },

  methods: {
    selectForDelete(item) {
      this.selectedForDelete = item
      this.deleteConfirmShow = true
    },

    onDeleteConfirm() {
      this.deleteConfirmShow = false
      this.deleteItem(this.selectedForDelete)
    },

    onDeleteCancel() {
      this.deleteConfirmShow = false
      this.selectedForDelete = null
    },

    reset() {
      this.updateReset()
      this.delReset()
      this.createReset()
    },

    edit(item) {
      this.selectedOrganization = item
      this.showOrganizationUpdateDialog = true
    },

    getPaginatedPage(options) {
      let query = '/organizations' + '?itemsPerPage=' + options.itemsPerPage + '&page=' + options.page

      if (options.sortBy.length > 0) {
        query = query + '&order[' + options.sortBy[0] + ']=' + (options.sortDesc[0] ? 'desc' : 'asc')
      }

      this.getPage(query)
    },

    ...mapActions({
      getPage: 'organization/list/default',
      deleteItem: 'organization/del/del',
      createReset: 'organization/create/reset',
      delReset: 'organization/del/reset',
      updateReset: 'organization/update/reset'
    })
  }
}
</script>
